import { useCallback, useEffect, useState } from 'react';
import { ClassVisitorRecord } from '../mocks/classVisitors';
import { classVisitorService, SaveClassVisitorInput } from '../services/classVisitor.service';

export const useClassLessonVisitors = (classId?: string | null, lessonId?: string | null, trimesterId?: string | null) => {
  const [classVisitors, setClassVisitors] = useState<ClassVisitorRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVisitors = useCallback(async () => {
    if (!classId || !lessonId || !trimesterId) {
      setClassVisitors([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const visitors = await classVisitorService.listByLesson(classId, lessonId, trimesterId);
      setClassVisitors(visitors);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Nao foi possivel carregar os visitantes.');
    } finally {
      setIsLoading(false);
    }
  }, [classId, lessonId, trimesterId]);

  useEffect(() => {
    loadVisitors();
  }, [loadVisitors]);

  const createVisitor = async (input: SaveClassVisitorInput) => {
    if (!classId || !lessonId) {
      throw new Error('Aula nao encontrada.');
    }

    const visitor = await classVisitorService.createForLesson(classId, lessonId, input);
    setClassVisitors((current) => [visitor, ...current.filter((item) => item.id !== visitor.id)]);

    return visitor;
  };

  const removeVisitor = async (visitorId: string) => {
    if (!classId || !lessonId || !trimesterId) {
      throw new Error('Aula nao encontrada.');
    }

    await classVisitorService.removeFromLesson(classId, lessonId, trimesterId, visitorId);
    setClassVisitors((current) => current.filter((visitor) => visitor.id !== visitorId));
  };

  const convertVisitorToStudent = async (visitorId: string, enrolledAt: string) => {
    if (!classId || !lessonId || !trimesterId) {
      throw new Error('Aula nao encontrada.');
    }

    const visitor = await classVisitorService.convertToStudent(classId, lessonId, trimesterId, visitorId, enrolledAt);
    setClassVisitors((current) => current.map((item) => (item.id === visitor.id ? visitor : item)));

    return visitor;
  };

  return {
    classVisitors,
    isLoading,
    error,
    reloadVisitors: loadVisitors,
    createVisitor,
    removeVisitor,
    convertVisitorToStudent,
  };
};
